import React, { useState, useEffect } from 'react';
import axios from 'axios';

const ChefTrackingMap = ({ bookingId }) => {
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (bookingId) {
      loadTrackingData();
      // Poll for updates every 10 seconds
      const interval = setInterval(loadTrackingData, 10000);
      return () => clearInterval(interval);
    }
  }, [bookingId]);

  const loadTrackingData = async () => {
    try {
      const response = await axios.get(`/tracking/journey/${bookingId}`);
      setTrackingData(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load tracking data:', error);
      setLoading(false);
    }
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      not_started: { icon: '⏳', text: 'Not Started', color: 'gray' },
      going_to_store: { icon: '🚗', text: 'Going to Store', color: 'blue' },
      at_store: { icon: '🛒', text: 'Shopping', color: 'purple' },
      leaving_store: { icon: '📦', text: 'Leaving Store', color: 'yellow' },
      en_route: { icon: '🚙', text: 'On the Way', color: 'orange' },
      arrived: { icon: '✅', text: 'Arrived', color: 'green' }
    };
    return statusMap[status] || statusMap.not_started;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Loading tracking data...</p>
      </div>
    );
  }

  if (!trackingData) {
    return (
      <div className="bg-white rounded-lg shadow p-6 text-center">
        <p className="text-gray-600">No tracking data available</p>
      </div>
    );
  }

  const statusInfo = getStatusInfo(trackingData.current_status);
  const lastLocation = trackingData.last_location;

  return (
    <div className="bg-white rounded-lg shadow-lg">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-600 to-orange-500 text-white px-6 py-4 rounded-t-lg">
        <h2 className="text-2xl font-bold flex items-center gap-3">
          <span className="text-3xl">📍</span>
          Live Chef Tracking
        </h2>
      </div>

      {/* Current Status */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`text-6xl`}>{statusInfo.icon}</div>
            <div>
              <p className="text-sm text-gray-600 font-medium">Current Status</p>
              <p className="text-2xl font-bold text-gray-900">{statusInfo.text}</p>
            </div>
          </div>
          <div className={`px-4 py-2 rounded-full bg-${statusInfo.color}-100 text-${statusInfo.color}-800 font-semibold`}>
            Live
          </div>
        </div>
      </div>

      {/* Map Placeholder */}
      <div className="relative bg-gray-100" style={{ height: '300px' }}>
        {lastLocation ? (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-blue-100 to-green-100">
            <div className="text-center">
              <div className="text-6xl mb-4">🗺️</div>
              <p className="text-gray-700 font-semibold">Chef Location</p>
              <p className="text-sm text-gray-600 mt-2">
                Lat: {lastLocation.latitude?.toFixed(4)}, Lon: {lastLocation.longitude?.toFixed(4)}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Updated: {lastLocation.updated_at ? new Date(lastLocation.updated_at).toLocaleTimeString() : 'N/A'}
              </p>
            </div>
          </div>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center text-gray-500">
              <div className="text-6xl mb-4">🗺️</div>
              <p>Waiting for location update...</p>
            </div>
          </div>
        )}
        
        {/* Full Map Integration Note */}
        <div className="absolute bottom-2 right-2 bg-white px-3 py-1 rounded shadow text-xs text-gray-600">
          💡 Google Maps integration can be added with API key
        </div>
      </div>

      {/* Journey Timeline */}
      <div className="p-6">
        <h3 className="font-bold text-lg text-gray-900 mb-4">Journey Timeline</h3>
        <div className="space-y-4">
          {trackingData.tracking_history && trackingData.tracking_history.length > 0 ? (
            trackingData.tracking_history.slice(-5).reverse().map((entry, index) => {
              const entryStatus = getStatusInfo(entry.status);
              return (
                <div key={index} className="flex items-start gap-3">
                  <div className="text-2xl">{entryStatus.icon}</div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">{entryStatus.text}</p>
                    {entry.store_name && (
                      <p className="text-sm text-gray-600">📍 {entry.store_name}</p>
                    )}
                    {entry.notes && (
                      <p className="text-sm text-gray-600 italic">{entry.notes}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(entry.timestamp).toLocaleString()}
                    </p>
                  </div>
                </div>
              );
            })
          ) : (
            <p className="text-gray-500 text-center py-4">No journey updates yet</p>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="bg-gray-50 px-6 py-4 rounded-b-lg border-t border-gray-200">
        <div className="flex justify-between text-sm">
          <div>
            <p className="text-gray-600">Total Updates</p>
            <p className="font-bold text-gray-900">{trackingData.total_updates || 0}</p>
          </div>
          <div>
            <p className="text-gray-600">Last Update</p>
            <p className="font-bold text-gray-900">
              {lastLocation?.updated_at 
                ? new Date(lastLocation.updated_at).toLocaleTimeString()
                : 'N/A'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChefTrackingMap;
